/**
 * ProjectWorklistGroup — one enrolled project's header + nested worklist
 * (batch) rows, rendered in the annotator category page's "Your projects"
 * section. Markup mirrors mockup `#s-acat` `.pgroup` / `.phead` / `.wl`
 * (PLANS/reviews/2026-07-07-labeling-overview-redesign-mockup.html).
 *
 * `worklists` is the caller's own enrollment record(s) for this project.
 * `GET /my-enrollments` returns one row per (project, annotator) pair, so
 * in practice this is almost always a single-item array today — the prop
 * stays a list so a future backend that surfaces per-batch history (the
 * mockup shows a completed Phase 1 batch alongside an active Phase 2 one)
 * slots in without a shape change here.
 *
 * `MyEnrollment` (src/types/factory.ts) doesn't carry a numeric done/total
 * case count from `/my-enrollments` — only `phase` + `state`. Rather than
 * fabricate a misleadingly precise fraction (no silent fallback), the
 * meter renders a binary in-progress/complete indicator (0/1 vs 1/1)
 * derived from `state`.
 *
 * The nested rows intentionally don't reuse `SquareRow` (unlike the header,
 * which does) — `SquareRow`'s fixed `px-5` padding has no room for the
 * mockup's `.wl` `pl-8` nested indent, and this task doesn't touch
 * `primitives.tsx`.
 */

import type { EnrollmentState, MyEnrollment } from "../../../types/factory";
import { ProgressMeter, SquareList, SquareRow, StatusBadge, type StatusTone } from "./primitives";

const STATE_SUB_LABEL: Record<EnrollmentState, string> = {
  active: "active",
  pending_approval: "submitted for review",
  blocked_pool_empty: "blocked (pool empty)",
  completed: "completed",
};

function headerBadge(state: EnrollmentState, phase: number): { tone: StatusTone; label: string } {
  if (state === "pending_approval") return { tone: "sig", label: "Under review" };
  if (state === "blocked_pool_empty") return { tone: "warn", label: "Blocked" };
  return { tone: "sig", label: `Phase ${phase}` };
}

interface ProjectWorklistGroupProps {
  project: MyEnrollment;
  worklists: MyEnrollment[];
  onOpenStudy: (worklistProjectId: string) => void;
}

export function ProjectWorklistGroup({
  project,
  worklists,
  onOpenStudy,
}: ProjectWorklistGroupProps) {
  const header = project.annotation_read_only
    ? { tone: "ghost" as const, label: "Paused · view only" }
    : headerBadge(project.state, project.phase);

  return (
    <SquareList>
      <SquareRow>
        <span aria-hidden="true" className="font-mono text-fg-faint">
          ◪
        </span>
        <span className="text-sm font-semibold text-fg">
          {project.project_name ?? project.project_id}
        </span>
        <span className="font-mono text-xs text-fg-muted">{project.project_id}</span>
        <span className="flex-1" />
        <StatusBadge tone={header.tone}>{header.label}</StatusBadge>
      </SquareRow>
      {worklists.map((w) => {
        const openable = !!w.worklist_project_id;
        const [done, total] = w.state === "active" ? [0, 1] : [1, 1];
        return (
          <button
            key={w.current_batch_id ?? `${w.project_id}-${w.state}`}
            type="button"
            onClick={() => openable && onOpenStudy(w.worklist_project_id as string)}
            disabled={!openable}
            className="flex w-full items-center gap-4 py-3.5 pl-8 pr-5 text-left transition hover:bg-white/2 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <span
              aria-hidden="true"
              className={`h-1.5 w-1.5 shrink-0 rounded-sharp ${
                w.state === "active" ? "bg-signal" : "bg-border-strong"
              }`}
            />
            <div className="min-w-0 flex-1">
              <div className="truncate text-sm text-fg">
                {w.current_batch_id ? `Batch ${w.current_batch_id}` : "No batch assigned yet"}
              </div>
              <div className="text-xs text-fg-muted">
                Phase {w.phase} ·{" "}
                {w.annotation_read_only ? "Paused · view only" : STATE_SUB_LABEL[w.state]}
              </div>
            </div>
            {!w.annotation_read_only && (
              <ProgressMeter
                done={done}
                total={total}
                tone={w.state === "active" ? "active" : "done"}
              />
            )}
            <span aria-hidden="true" className="text-fg-faint">
              →
            </span>
          </button>
        );
      })}
    </SquareList>
  );
}

export default ProjectWorklistGroup;
